import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, Package, DollarSign, Tag } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import productsService, { Product } from '../../services/crm/productsService';
import DetailPageLayout from '../../components/crm/DetailPageLayout';

const ProductCatalog: React.FC = () => {
  const { activeOrgId } = useCrmOrgStore();
  
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSku, setFormSku] = useState('');
  const [formCategory, setFormCategory] = useState<'product' | 'service' | 'subscription' | 'consulting'>('product');
  const [formUnitType, setFormUnitType] = useState('unit');
  const [formDefaultCost, setFormDefaultCost] = useState('');
  const [formDefaultPrice, setFormDefaultPrice] = useState('');

  const loadProducts = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const { data } = await productsService.list(activeOrgId);
      setProducts(data || []);
    } catch (error: any) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const resetForm = () => {
    setFormName('');
    setFormDescription('');
    setFormSku('');
    setFormCategory('product');
    setFormUnitType('unit');
    setFormDefaultCost('');
    setFormDefaultPrice('');
    setEditingProduct(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setFormName(product.name);
    setFormDescription(product.description || '');
    setFormSku(product.sku || '');
    setFormCategory(product.category || 'product');
    setFormUnitType(product.unit_type || 'unit');
    setFormDefaultCost(product.default_cost ? String(product.default_cost) : '');
    setFormDefaultPrice(product.default_price ? String(product.default_price) : '');
    setShowAddModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOrgId || !formName.trim()) return;

    try {
      const payload = {
        organization_id: activeOrgId,
        name: formName.trim(),
        description: formDescription.trim() || undefined,
        sku: formSku.trim() || undefined,
        category: formCategory,
        unit_type: formUnitType,
        default_cost: formDefaultCost ? parseFloat(formDefaultCost) : undefined,
        default_price: formDefaultPrice ? parseFloat(formDefaultPrice) : undefined,
        is_active: true
      };

      if (editingProduct) {
        await productsService.update(editingProduct.id, payload);
        toast.success('Product updated');
      } else {
        await productsService.create(payload);
        toast.success('Product created');
      }

      setShowAddModal(false);
      resetForm();
      loadProducts();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save product');
    }
  };

  const handleDelete = async (productId: string) => {
    if (!window.confirm('Delete this product? It will still appear in existing quotes.')) return;

    try {
      await productsService.delete(productId);
      toast.success('Product deleted');
      loadProducts();
    } catch (error: any) {
      toast.error('Failed to delete product');
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const calculateMargin = (cost?: number, price?: number) => {
    if (!cost || !price || price === 0) return null;
    return ((price - cost) / price * 100).toFixed(1);
  };

  return (
    <DetailPageLayout backLink="/crm#settings" backLabel="Back to Settings" title="Product Catalog">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Package className="w-7 h-7" />
              Product Catalog
            </h1>
            <p className="text-gray-600 mt-1">
              Manage products and services for quotes
            </p>
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchTerm ? 'No products found' : 'No products yet'}
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm ? 'Try a different search term' : 'Add your first product to start building quotes'}
            </p>
            {!searchTerm && (
              <button
                onClick={openAddModal}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                <Plus className="w-4 h-4" />
                Add First Product
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProducts.map((product) => {
              const margin = calculateMargin(product.default_cost, product.default_price);
              
              return (
                <div
                  key={product.id}
                  className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {product.name}
                      </h3>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                          {product.category || 'product'}
                        </span>
                        {product.sku && (
                          <span className="text-xs text-gray-500">SKU: {product.sku}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditModal(product)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {product.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {product.description}
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Default Cost</div>
                      <div className="font-semibold text-gray-900 flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        {product.default_cost ? product.default_cost.toFixed(2) : '--'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Default Price</div>
                      <div className="font-semibold text-gray-900 flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        {product.default_price ? product.default_price.toFixed(2) : '--'}
                      </div>
                    </div>
                  </div>

                  {margin && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Default Margin</span>
                        <span className={`text-sm font-semibold ${
                          parseFloat(margin) < 20 ? 'text-red-600' :
                          parseFloat(margin) < 40 ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {margin}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-bold mb-4">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Name *
                    </label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Website Design, SEO Package"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="What does this product/service include?"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SKU
                      </label>
                      <input
                        type="text"
                        value={formSku}
                        onChange={(e) => setFormSku(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Optional"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <select
                        value={formCategory}
                        onChange={(e) => setFormCategory(e.target.value as any)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="product">Product</option>
                        <option value="service">Service</option>
                        <option value="subscription">Subscription</option>
                        <option value="consulting">Consulting</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit Type
                    </label>
                    <select
                      value={formUnitType}
                      onChange={(e) => setFormUnitType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="unit">Unit</option>
                      <option value="hour">Hour</option>
                      <option value="day">Day</option>
                      <option value="month">Month</option>
                      <option value="license">License</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Default Cost (what you pay)
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formDefaultCost}
                          onChange={(e) => setFormDefaultCost(e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Default Price (what you charge)
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formDefaultPrice}
                          onChange={(e) => setFormDefaultPrice(e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>

                  {formDefaultCost && formDefaultPrice && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="text-sm text-gray-700">
                        Default Margin:{' '}
                        <span className="font-semibold text-blue-700">
                          {calculateMargin(parseFloat(formDefaultCost), parseFloat(formDefaultPrice))}%
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                      {editingProduct ? 'Save Changes' : 'Add Product'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </DetailPageLayout>
  );
};

export default ProductCatalog;
